import React, { useState, useEffect } from 'react';
import ImagePortal from './ImagePortal';
import VideoPortal from './VideoPortal';
import FilmStrip from './FilmStrip';
import { loadGalleryImages } from './imageUtils';
import './MainView.css';

const MainView = ({ onViewChange }) => {
    const [position, setPosition] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [images, setImages] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
    const [filmstripWidth, setFilmstripWidth] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [autoScrollPaused, setAutoScrollPaused] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    useEffect(() => {
        const loadImages = async () => {
            setIsLoading(true);
            setError(null);

            try {
                console.log('Loading images...');
                const loadedImages = await loadGalleryImages({
                    randomize: true,
                    useThumbnails: true,
                    limit: 25
                });

                console.log('Loaded images:', loadedImages);

                if (loadedImages.length === 0) {
                    console.warn('No images found in the gallery.');
                }

                setImages(loadedImages);

                // Rest of the function remains the same...
            } catch (error) {
                console.error('Error loading images:', error);
                setError('Failed to load images');
            } finally {
                setIsLoading(false);
            }
        };

        loadImages();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleScroll = (direction) => {
        /* console.log('Scroll attempt:', {
            imagesLength: images.length,
            isTransitioning,
            filmstripWidth,
            windowWidth,
            position
        }); */

        setIsTransitioning(true);

        const visibleWidth = windowWidth;
        const isMobile = windowWidth <= 768;
        const scrollAmount = isMobile ? visibleWidth * 0.8 : visibleWidth / 2;

        const effectiveFilmstripWidth = filmstripWidth;
        const maxScroll = -(effectiveFilmstripWidth - visibleWidth);

        setPosition(prevPosition => {
            let newPosition = prevPosition + (direction * scrollAmount);

            // If scrolling left and we've reached the end, reset to start
            if (direction === -1 && -newPosition >= effectiveFilmstripWidth - visibleWidth) {
                newPosition = 0;
            }
            // If scrolling right and we've reached the start, jump to end
            else if (direction === 1 && newPosition > 0) {
                newPosition = maxScroll;
            }
            // Ensure position stays within bounds
            else {
                newPosition = Math.max(maxScroll, Math.min(0, newPosition));
            }

            return newPosition;
        });

        // Clear the transitioning state after animation completes
        setTimeout(() => {
            setIsTransitioning(false);
        }, 100);
    };

    const handleNavigation = Object.assign(
        (direction) => handleScroll(direction),
        {
            setFilmstripWidth: (width) => {
                setFilmstripWidth(width);
            }
        }
    );

    // Regular auto-scroll effect
    useEffect(() => {
        // Only start auto-scrolling if images are loaded and width is set
        if (images.length === 0 || isLoading || autoScrollPaused || filmstripWidth === null) return;

        const scrollTimer = setInterval(() => {
            handleScroll(-1);
        }, 3000);

        return () => clearInterval(scrollTimer);
    }, [images, isLoading, autoScrollPaused, filmstripWidth]);

    const handleMediaClick = (media, index) => {
        if (index === undefined || index === null) {
            console.error('Media click received without index');
            return;
        }

        setSelectedMedia(media);  // Pass the whole media object, not just the path
        setSelectedMediaIndex(index);
        setAutoScrollPaused(true);

        console.log(`Media clicked:`, media);
    };

    const handleMediaClose = () => {
        setSelectedMedia(null);
        setSelectedMediaIndex(null);
        setAutoScrollPaused(false);
    };

    const handlePreviousMedia = () => {
        if (selectedMediaIndex === null) return;
        const newIndex = selectedMediaIndex === 0 ? images.length - 1 : selectedMediaIndex - 1;
        const newMedia = images[newIndex];
        setSelectedMediaIndex(newIndex);
        setSelectedMedia(newMedia);
    };

    const handleNextMedia = () => {
        if (selectedMediaIndex === null) return;
        const newIndex = selectedMediaIndex === images.length - 1 ? 0 : selectedMediaIndex + 1;
        const newMedia = images[newIndex];
        setSelectedMediaIndex(newIndex);
        setSelectedMedia(newMedia);
    };

    if (isLoading) {
        return (
            <div className="main-view-container">
                <div className="main-content">
                    <div className="title-container">
                        <h1 className="main-title">In Memory Of</h1>
                        <h2 className="name-title">Brock Phillip Currey</h2>
                        <h3 className="date-title">May 7, 1982 - December 6, 2024</h3>
                    </div>
                    <div className="loading-indicator">Loading photos...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main-view-container">
                <div className="main-content">
                    <div className="title-container">
                        <h1 className="main-title">In Memory Of</h1>
                        <h2 className="name-title">Brock Phillip Currey</h2>
                        <h3 className="date-title">May 7, 1982 - December 6, 2024</h3>
                    </div>
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-view-container">
            <div className="main-content">
                <div className="title-container">
                    <h1 className="main-title">In Memory Of</h1>
                    <h2 className="name-title">Brock Phillip Currey</h2>
                    <h3 className="date-title">May 7, 1982 - December 6, 2024</h3>
                </div>

                {isLoading ? (
                    <div className="loading-indicator">Loading photos...</div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <>
                        <FilmStrip
                            images={images || []}  // Ensure we always pass an array
                            width={windowWidth}
                            transform={position}
                            showNavButtons={true}
                            onNavClick={handleNavigation}
                            onImageClick={handleMediaClick}
                        />

                        <div className="main-button-container">
                            <button
                                onClick={() => onViewChange('viewPhotos')}
                                className="main-btn main-btn-black"
                            >
                                View Photos
                            </button>
                            {/* Uploads disabled: uncomment to turn them back on
                            <button
                                onClick={() => onViewChange('uploadPhotos')}
                                className="main-btn main-btn-black"
                            >
                                Upload Photos
                            </button>
                            */}
                        </div>
                    </>
                )}
            </div>

            {selectedMedia && (
                selectedMedia.type === 'video' ? (
                    <VideoPortal
                        video={selectedMedia.originalPath}
                        onClose={handleMediaClose}
                        onPrevious={handlePreviousMedia}
                        onNext={handleNextMedia}
                        totalItems={images.length}
                        currentIndex={selectedMediaIndex}
                    />
                ) : (
                    <ImagePortal
                        image={selectedMedia.originalPath}
                        onClose={handleMediaClose}
                        onPrevious={handlePreviousMedia}
                        onNext={handleNextMedia}
                        totalImages={images.length}
                        currentIndex={selectedMediaIndex}
                    />
                )
            )}
        </div>
    );
};

export default MainView;